import React, { useContext, useRef } from "react";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import * as Yup from "yup";
import { Formik, FormikHelpers } from "formik";
import { ScreenFlowFormButtons } from "./ScreenFlowFormButtons";
import { objectVals2String } from "../../../utils/objectVals2String";
import { Country, State } from "../../../models/Geography";
import { SelectChangeEvent } from "@mui/material/Select/SelectInput";
import { AddressAutocompleteForm } from "./AddressAutocompleteForm";
import { Place } from "../../../models/Place";
import { useAuthContext } from "AuthContextWrapper"
import { stateLabelMappings } from "../../../models/Geography";

interface Values {
  noAddress: boolean;
  address: string; // from place.formattedAddress
  countryId?: number;
  stateId?: number;
}

interface Props {
  initPlace?: Place;
  initCountryId?: number;
  initStateId?: number;
  addressLabel?: string;
  onComplete: (
    place?: Place,
    countryId?: number,
    stateId?: number
  ) => Promise<boolean>;
  onClear?: () => Promise<void>;
  onBack: () => void;
}

export const SingleAddressForm = ({
  initPlace,
  initCountryId,
  initStateId,
  addressLabel,
  onComplete,
  onClear,
  onBack,
}: Props): JSX.Element => {
  const { countries, states } = useAuthContext()

  const thePlace = useRef<Place | undefined>(initPlace);
  // don't have a formatted address and do have a country and state (then convert to boolean)
  const showCountryNStateSelect = !!(
    !initPlace?.formattedAddress &&
    initCountryId &&
    initStateId
  );
  // uncomment to debug
  // console.log("initPlace", initPlace);

  // https://formik.org/docs/api/formik
  // https://github.com/jquense/yup#schemawhenkeys-string--string-builder-object--values-any-schema--schema-schema
  const formikInit = {
    values: {
      noAddress: showCountryNStateSelect,
      address: initPlace?.formattedAddress || "",
      countryId: initCountryId,
      stateId: initCountryId ? initStateId : undefined,
    } as Values,
    schema: Yup.object().shape({
      address: Yup.string().when("noAddress", (noAddress) => {
        return noAddress
          ? Yup.string()
          : Yup.string().required(
              "Please enter a valid address, or let us know if you don't know it"
            );
      }),
      countryId: Yup.number().when("noAddress", (noAddress) => {
        return noAddress
          ? Yup.number().required("Country is required")
          : Yup.number();
      }),
      stateId: Yup.number().when("noAddress", (noAddress) => {
        return noAddress
          ? Yup.number().required("State is required")
          : Yup.number();
      }),
    }),
  };

  const stateLabel = (countryId = 1): string => {
    const country = countries?.find((c) => c.id == countryId);
    const label = stateLabelMappings[country?.slug || "State"];
    return label;
  };

  const handleSubmit = async (
    values: Values,
    { setSubmitting, setStatus }: FormikHelpers<Values>
  ) => {
    setStatus("");
    try {
      // validation:
      //  if !noAddress have Place
      //  else have countryId & stateId
      if (!values.noAddress) {
        if (thePlace.current instanceof Place) {
          await onComplete(thePlace.current);
        } else {
          throw Error("Please enter and select an address");
        }
      } else {
        await onComplete(undefined, values.countryId, values.stateId);
      }
    } catch (error) {
      setStatus("Sorry, error: " + ((error as Error)?.message || ""));
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={formikInit.values}
      validationSchema={formikInit.schema}
      onSubmit={handleSubmit}
      key={objectVals2String(formikInit.values)}
    >
      {({
        errors,
        handleChange,
        handleSubmit,
        isSubmitting,
        touched,
        values,
        status,
        setFieldValue,
      }) => {
        return (
          <form onSubmit={handleSubmit}>
            <Grid container>
              {!values.noAddress && (
                <Grid item xs={12}>
                  <AddressAutocompleteForm
                    inputLabelOnTop={true}
                    initId={initPlace && initPlace.id}
                    initAddress={initPlace && initPlace.formattedAddress}
                    addressLabel={addressLabel}
                    onComplete={async (place: Place) => {
                      setFieldValue("address", place?.formattedAddress);
                      thePlace.current = place;
                    }}
                    onClear={() => {
                      setFieldValue("address", "");
                      thePlace.current = undefined;
                      onClear?.();
                    }}
                  />
                  {touched.address && errors.address && (
                    <FormHelperText
                      sx={{
                        color: "error.main",
                      }}
                    >
                      {errors.address}
                    </FormHelperText>
                  )}
                </Grid>
              )}
              {values.noAddress && (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-around",
                      py: 1,
                      maxWidth: 600,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 2,
                      }}
                    >
                      At least country, and state/province/territory must be
                      known.
                      {/*to determine probate type.*/}
                    </Typography>
                    <FormControl
                      fullWidth
                      sx={{
                        my: 2,
                      }}
                    >
                      <InputLabel id="country-select-label">Country</InputLabel>
                      <Select
                        labelId="country-select-label"
                        id="country-select"
                        name="countryId"
                        value={values.countryId || ""}
                        label="Country"
                        onChange={async (
                          event: SelectChangeEvent<any>
                        ): Promise<any> => {
                          setFieldValue("stateId", undefined);
                          return handleChange(event);
                        }}
                        error={Boolean(touched.countryId && errors.countryId)}
                      >
                        {countries?.map((country: Country) => (
                          <MenuItem
                            value={country.id}
                            key={`country_${country.name}`}
                          >
                            {country.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {touched.countryId && errors.countryId && (
                        <FormHelperText
                          sx={{
                            color: "error.main",
                          }}
                        >
                          {errors.countryId}
                        </FormHelperText>
                      )}
                    </FormControl>
                    <FormControl
                      fullWidth
                      sx={{
                        mt: 2,
                      }}
                    >
                      <InputLabel id="state-select-label">
                        {stateLabel(values.countryId)}
                      </InputLabel>
                      {states && (
                        <Select
                          labelId="state-select-label"
                          id="state-select"
                          name="stateId"
                          value={values.stateId || ""}
                          label={stateLabel(values.countryId)}
                          onChange={handleChange}
                          error={Boolean(touched.stateId && errors.stateId)}
                        >
                          {states
                            .filter((s) => s.countryId == values.countryId)
                            .map((state: State) => (
                              <MenuItem
                                value={state.id}
                                key={`state_${state.name}`}
                              >
                                {state.name}
                              </MenuItem>
                            ))}
                        </Select>
                      )}
                      {touched.stateId && errors.stateId && (
                        <FormHelperText
                          sx={{
                            color: "error.main",
                          }}
                        >
                          {errors.stateId}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Box>
                </Grid>
              )}
              <Grid item xs={12}>
                <Button
                  variant="text"
                  color="secondary"
                  onClick={() => {
                    setFieldValue("noAddress", !values.noAddress);
                  }}
                >
                  {!values.noAddress
                    ? "I don’t know the address"
                    : "I know the address"}
                </Button>
              </Grid>
              <Grid item xs={12}>
                <ScreenFlowFormButtons
                  isSubmitting={isSubmitting}
                  status={status}
                  onBack={onBack}
                />
              </Grid>
            </Grid>
          </form>
        );
      }}
    </Formik>
  );
};
